// The cue answers "what happens now". Every surface reads it: the cue bar
// writes the words, the board decides which squares are hot and which pieces
// dim, the die decides whether it is a button. One function, no second
// opinion anywhere in the UI.

import { KIND_NAMES } from '../engine/abilities';
import { hungryMovers, isSquareAttacked } from '../engine/moves';
import { squareName } from '../engine/squares';
import type { Side } from '../engine/types';
import { OWNER_NAMES } from '../spells/helpers';
import { spellById } from '../spells/library';
import type { SpellPolarity } from '../spells/types';
import { eligibleTargets, legalMovesFrom, positionOf } from './flow';
import { canPayForRoll, energyOf, type GameState, rollCost, sideTurnNumber } from './state';

export type CueKind = 'roll' | 'aim' | 'move' | 'move-again' | 'wait' | 'over' | 'review';

export type Cue = {
  kind: CueKind;
  /** The player must act. */
  mine: boolean;
  /** The player may start a roll now: one that is owed, or one the energy pays for. */
  canRoll: boolean;
  headline: string;
  detail: string;
  /** Squares that answer a click right now. */
  hot: readonly number[];
  /** Pieces that stay bright. Null keeps every piece bright. */
  spotlight: readonly number[] | null;
  polarity: SpellPolarity | null;
  danger: boolean;
};

const ownMovableSquares = (state: GameState): number[] => {
  const result: number[] = [];
  state.board.forEach((piece, square) => {
    if (piece?.owner === state.turn && legalMovesFrom(state, square).length > 0) {
      result.push(square);
    }
  });
  return result;
};

const royalInDanger = (state: GameState, side: Side): boolean => {
  const attacker: Side = side === 'gold' ? 'violet' : 'gold';
  const position = positionOf(state);
  return state.board.some(
    (piece, square) =>
      piece?.owner === side && piece.royal && isSquareAttacked(position, square, attacker),
  );
};

const quiet = (kind: CueKind, headline: string, detail: string): Cue => ({
  kind,
  mine: false,
  canRoll: false,
  headline,
  detail,
  hot: [],
  spotlight: null,
  polarity: null,
  danger: false,
});

/** Plain words, not final copy. */
const energyLine = (state: GameState, side: Side): string =>
  `Energy ${energyOf(state, side)}. A cast costs ${rollCost(state, side)}.`;

/**
 * Plain words, not final copy. Godsmode: the pips of the caster show the
 * energy, so the detail does not repeat the count.
 */
const ENERGY_ROLL_HEADLINE = 'Cast for energy';
const ENERGY_ROLL_DETAIL = 'The die gives energy or a scroll.';

/**
 * The headline of a move of the player. Garrett's words, 2026-10-13: "\"Your
 * move\" becomes \"You're up\" or \"Back to it\" or \"Let's get them\"".
 * The turn picks one, so the headline holds for the whole turn and a replay
 * gives the same words. The first turn has the first.
 */
const MOVE_HEADLINES = ["You're up", 'Back to it', "Let's get them"] as const;

const moveHeadline = (state: GameState): string =>
  MOVE_HEADLINES[(sideTurnNumber(state) - 1) % MOVE_HEADLINES.length] as string;

/**
 * Garrett's words, 2026-10-13, for Godsmode: the council is the pieces that
 * give the pitches. A match with a wheel has no pitch, so it keeps the plain line.
 */
const MOVE_DETAIL_GODS = 'Move a piece or heed your recon knights.';
/** Plain words, not final copy. */
const MOVE_DETAIL = 'Pick a piece, then a square.';

/** The danger comes first: the seals and the band in the frame say that no piece can capture. */
const moveLine = (state: GameState, danger: boolean): string => {
  if (danger) return 'Your royal piece is under attack.';
  if (state.rules.noCapturePlies > 0) return 'No piece can capture this turn.';
  return state.mode === 'gods' ? MOVE_DETAIL_GODS : MOVE_DETAIL;
};

export const cueFor = (state: GameState, player: Side): Cue => {
  const mine = state.turn === player;
  const them = OWNER_NAMES[state.turn];

  if (state.phase === 'over') {
    const winner = state.winner ? OWNER_NAMES[state.winner] : 'Nobody';
    return quiet('over', `${winner} wins`, state.endLine || 'Start a new match from the menu.');
  }

  if (state.phase === 'roll') {
    if (!mine) return quiet('wait', `${them} casts the die`, 'Watch the wheel.');
    // The roll phase holds only a roll the side is owed. A roll for energy starts from the move.
    const freeRoll = state.lastCast?.hadNoEffect && state.freeRollUsed;
    // In Godsmode the one roll of a turn gives the energy.
    const casters = state.mode === 'gods';
    return {
      kind: 'roll',
      mine: true,
      canRoll: true,
      headline: casters ? ENERGY_ROLL_HEADLINE : 'Cast again',
      detail: casters
        ? ENERGY_ROLL_DETAIL
        : freeRoll
          ? 'The spell had no effect. This cast is free.'
          : `Wild Magic. ${state.pendingRolls} casts left.`,
      hot: [],
      spotlight: null,
      polarity: null,
      danger: false,
    };
  }

  if (state.phase === 'aim' && state.pendingSpell) {
    const spell = spellById(state.pendingSpell);
    if (!mine) {
      return {
        ...quiet('wait', `${them} aims ${spell.name}`, spell.blurb),
        polarity: spell.polarity,
      };
    }
    const targets = eligibleTargets(state);
    const picksLeft = spell.targetCount - state.pendingTargets.length;
    return {
      kind: 'aim',
      mine: true,
      canRoll: false,
      // The instruction is the headline. The card and the lit wheel face already name the spell.
      headline: spell.prompt.replace(/\.$/, ''),
      detail: picksLeft > 1 ? `${spell.name}. ${picksLeft} picks left.` : spell.name,
      hot: targets,
      spotlight: [...targets, ...state.pendingTargets],
      polarity: spell.polarity,
      danger: false,
    };
  }

  if (state.phase === 'move') {
    if (!mine) return quiet('wait', `${them} is thinking`, '');
    const danger = royalInDanger(state, player);
    if (state.mustMovePieceId !== null) {
      const square = state.board.findIndex((piece) => piece?.id === state.mustMovePieceId);
      const piece = state.board[square];
      const name = piece ? KIND_NAMES[piece.kind] : 'piece';
      return {
        kind: 'move-again',
        mine: true,
        canRoll: false,
        headline: state.moveAgainSpellId ? spellById(state.moveAgainSpellId).name : 'Double Time',
        detail: `Move the ${name} on ${squareName(square)} again.`,
        hot: [square, ...legalMovesFrom(state, square).map((move) => move.to)],
        spotlight: [square],
        polarity: 'ally',
        danger,
      };
    }
    const canRoll = canPayForRoll(state);
    const mustCapture = hungryMovers(positionOf(state), player);
    if (mustCapture.length > 0) {
      const square = mustCapture[0] as number;
      const piece = state.board[square];
      const name = piece ? KIND_NAMES[piece.kind] : 'piece';
      return {
        kind: 'move',
        mine: true,
        canRoll,
        headline: moveHeadline(state),
        detail: `The mustCapture ${name} on ${squareName(square)} must capture. Pick its meal.`,
        hot: ownMovableSquares(state),
        spotlight: mustCapture,
        polarity: 'enemy',
        danger,
      };
    }
    const movable = ownMovableSquares(state);
    return {
      kind: 'move',
      mine: true,
      canRoll,
      headline: moveHeadline(state),
      // In Godsmode the pips of the caster have the energy, and a cast has no one cost.
      detail:
        state.mode === 'gods'
          ? moveLine(state, danger)
          : `${moveLine(state, danger)} ${energyLine(state, player)}`,
      hot: movable,
      // Only a piece that can move stays bright. Every enemy dims.
      spotlight: movable,
      polarity: null,
      danger,
    };
  }

  return quiet('wait', '', '');
};

/** The cue while the board shows an earlier step. Nothing answers a click. */
/** The match waits: for the casters of its first match, or for the coin flip of a room. Nobody acts. */
export const holdCue = (): Cue => quiet('wait', '', '');

export const reviewCue = (line: string, step: number, lastStep: number): Cue =>
  quiet('review', step === 0 ? 'Viewing the start' : `Viewing step ${step} of ${lastStep}`, line);
